import React from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Button, Card } from '@chargify/custom-ui-components';
import { useDeviceInfo } from '@chargify/device-provider';

import { ErrorContainer, RetryButton } from './ContentLoader.styles';
import { SubscriptionManagerLoader } from '../SubscriptionManagerLoader';

export type ContentLoaderProps = {
  /**
   * Application loading indicator
   */
  isLoading: boolean;
  /**
   * Error occured during subscriptions loading
   */
  loadingError: boolean;
  /**
   * Flag that indicates content presentation
   */
  contentReady: boolean;
  /**
   * Error notification message
   */
  errorMessage: string;
  /**
   * Refetch content event handler
   */
  onReinitialize: () => void;
  /**
   * React children nodes
   */
  children: React.ReactNode;
};

const ContentLoader = ({
  isLoading,
  loadingError,
  contentReady,
  errorMessage,
  onReinitialize,
  children,
}: ContentLoaderProps) => {
  const { t } = useTranslation();
  const { device } = useDeviceInfo();

  if (isLoading) {
    return <SubscriptionManagerLoader />;
  }

  if (loadingError) {
    return (
      <Card>
        <Card.Body>
          <ErrorContainer paddingTop={[5, 6]} paddingBottom={5}>
            <BodyText variant={device === 'mobile' ? 'body2' : 'body1'}>
              {errorMessage}
            </BodyText>
            <RetryButton marginTop={6}>
              <Button
                label={t('retry', { ns: 'common' })}
                onClick={onReinitialize}
              />
            </RetryButton>
          </ErrorContainer>
        </Card.Body>
      </Card>
    );
  }

  if (contentReady) return <>{children}</>;

  return null;
};

export default ContentLoader;
